"use client";

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Issue } from "@/types/issues";
import AssigneeDropdown from "./assignee-dropdown";
import { DueDateBadge } from "@/components/common/badges/due-date-badge";
import { EstimateBadge } from "@/components/common/badges/estimate-badge";
import { LabelBadge } from "@/components/common/badges/label-badge";
import { Status } from "@/types/status";
import { cn } from "@/lib/utils";
import { useFocusStore } from "@/store/zustand/focusStore";
import { PriorityBadge } from "@/components/common/badges/priority-badge";
import { shouldIgnoreMouseHover } from "@/hooks/useIssueKeyboardShortcuts";
import { LabelInterface } from "@/types/labels";
import { StatusBadge } from "../badges/status-badge";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";
import { useContextMenuStore } from "@/store/zustand/contextMenuStore";
import { resolveIssueStatus } from "@/utils/filterUtils";
import { Project } from "@/utils/types";

type IssueGridProps = {
  issue: Issue;
  users?: any[];
  labels?: any[];
  statuses: Status[];
  onClick?: () => void;
  provided?: any;
  snapshot?: any;
  icon?: () => JSX.Element;
  issueRefs?: { [key: string]: HTMLDivElement };
  visibleIssueIds?: string[]; // For selection range calculations
  project?: Project;
  isDragging: boolean;
};

// Configure hover delay (in milliseconds)
const HOVER_DELAY = 300; // Increased delay to give more time for mouse to settle

// Memoize badge components for better performance
const MemoizedStatusBadge = React.memo(StatusBadge);
const MemoizedPriorityBadge = React.memo(PriorityBadge);
const MemoizedEstimateBadge = React.memo(EstimateBadge);
const MemoizedLabelBadge = React.memo(LabelBadge);
const MemoizedDueDateBadge = React.memo(DueDateBadge);

export const IssueGrid = React.forwardRef<HTMLDivElement, IssueGridProps>(
  (
    {
      issue,
      users = [],
      labels = [],
      statuses = [],
      onClick,
      provided,
      snapshot,
      icon,
      issueRefs,
      visibleIssueIds,
      project,
      isDragging,
    },
    ref,
  ) => {
    const statusNames = useMemo(() => statuses.map((s) => s.name), [statuses]);
    // The number of labels to show before starting to stack
    const LABEL_STACK_THRESHOLD = 5;
    // Once stacking starts, the number of labels to show before the stack
    const PRE_STACK_LABEL_COUNT = 4;

    const setLastFocusedIssue = useFocusStore(
      (state) => state.setLastFocusedIssue,
    );
    const focusedIssueId = useFocusStore((state) => state.focusedIssueId);
    const contextMenuIssueIds = useContextMenuStore(
      (state) => state.contextMenuIssueIds,
    );

    const isFocused = focusedIssueId === issue.canvas.id;

    // Issue selection hook
    const selectedIssueIds = useIssueSelectionStore(
      (state) => state.selectedIssueIds,
    );
    const isSelected = selectedIssueIds.has(issue.canvas.id);

    // Use a ref for the hover timer
    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = useCallback(() => {
      if (shouldIgnoreMouseHover()) return;

      // Start the hover timer
      hoverTimerRef.current = setTimeout(() => {
        setLastFocusedIssue(issue.canvas.id);
      }, HOVER_DELAY);
    }, [issue.canvas.id, setLastFocusedIssue]);

    const handleMouseLeave = useCallback(() => {
      // Clear the hover timer if it exists
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    }, []);

    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
        }
      };
    }, []);

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        // Set last focused issue on any direct interaction
        setLastFocusedIssue(issue.canvas.id);
        // Handle selection logic
        onClick && onClick();
      },
      [issue.canvas.id, onClick],
    );

    // Only apply special drag styling if we're actually dragging
    const style = snapshot?.isDragging
      ? {
          ...provided?.draggableProps?.style,
          cursor: "grabbing",
          boxShadow: "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
          opacity: 0.9,
        }
      : {
          ...provided?.draggableProps?.style,
          cursor: "default",
        };

    const shouldHighlight = contextMenuIssueIds.includes(issue.canvas.id);

    return (
      <div
        ref={(el) => {
          // Handle the forwarded ref
          if (typeof ref === "function") {
            ref(el);
          } else if (ref) {
            ref.current = el;
          }
          // Store in our issue refs map
          if (el && issueRefs) {
            issueRefs[issue.canvas.id] = el;
          }
          // Handle the drag and drop ref
          if (provided?.innerRef) {
            provided.innerRef(el);
          }
        }}
        {...(provided?.draggableProps || {})}
        {...(provided?.dragHandleProps || {})}
        className={cn(
          "w-full bg-secondary-n-950 rounded-lg shadow-2xs cursor-default flex flex-col gap-1 p-2 relative",
          !isSelected && "transition-colors hover:bg-secondary-n-900",
          isFocused && "ring-1 ring-primary-0 ring-inset",
          shouldHighlight && "bg-secondary-n-900",
          isSelected && "bg-primary-n-600/20",
          snapshot?.isDragging && "shadow-lg",
          // Add fade effect to selected items when a drag is happening but this item is not being dragged
          isDragging &&
            !snapshot?.isDragging &&
            isSelected &&
            "opacity-50 transition-opacity duration-200",
        )}
        style={style}
        data-issue-id={issue.canvas.id}
        data-focused={isFocused ? "true" : "false"}
        data-selected={isSelected ? "true" : "false"}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onContextMenu={() => {
          setLastFocusedIssue(issue.canvas.id);
          const selectedIds = Array.from(selectedIssueIds);
          useContextMenuStore
            .getState()
            .openContextMenuForIssues(
              selectedIssueIds.size > 0 ? selectedIds : [issue.canvas.id],
            );
        }}
      >
        {/* Add count indicator when dragging and multiple items are selected */}
        {snapshot?.isDragging && selectedIssueIds.size > 1 && isSelected && (
          <div className="absolute -top-2 -right-2 bg-secondary-n-950 text-white text-xs font-medium rounded-sm min-w-[20px] h-5 flex items-center justify-center px-1.5 ring-1 ring-secondary-n-700">
            {selectedIssueIds.size}
          </div>
        )}
        {/* --- TOP ROW: Status Icon, Identifier, Assignee --- */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 min-w-0">
            <span className="body text-xs font-normal text-secondary-n-400 truncate max-w-[100px]">
              {issue.canvas.current_branch}
            </span>
          </div>
          <AssigneeDropdown
            issueId={issue.canvas.id}
            users={users}
            value={issue.canvas.assigned_user_id ?? null}
            size={20}
            className="size-5"
          />
        </div>
        {/* --- TITLE --- */}
        <div className="flex items-start gap-1">
          <MemoizedStatusBadge
            status={statuses.find(
              (status) =>
                status.name ===
                resolveIssueStatus(issue.featurePrd?.status, statusNames),
            )}
            issueId={issue.canvas.id}
            statuses={statuses}
            project={project}
          />
          <span
            className="body text-[13px] font-medium line-clamp-2 mt-0.5"
            data-testid="canvas-card-title"
          >
            {issue.featurePrd?.title || ""}
          </span>
        </div>
        {/* --- BOTTOM ROW: Labels, Priority, Estimate, Due Date --- */}
        <div className="flex items-center gap-1 flex-wrap">
          {/* Priority badge */}
          <MemoizedPriorityBadge
            priority={issue.featurePrd?.priority}
            issueId={issue.canvas.id}
            hasOutline={true}
          />
          {/* estimate badge */}
          {issue.featurePrd?.estimate && issue.featurePrd.estimate > 0 && (
            <MemoizedEstimateBadge
              estimate={issue.featurePrd.estimate}
              issueId={issue.canvas.id}
            />
          )}
          {/* Labels */}
          {issue.canvas.labels && issue.canvas.labels.length > 0 ? (
            issue.canvas.labels.length <= LABEL_STACK_THRESHOLD ? (
              issue.canvas.labels.map((label) => (
                <MemoizedLabelBadge
                  key={label}
                  labelId={label}
                  issue={issue}
                  projectLabels={labels}
                />
              ))
            ) : (
              <>
                {issue.canvas.labels
                  .slice(0, PRE_STACK_LABEL_COUNT)
                  .map((label) => (
                    <MemoizedLabelBadge
                      key={label}
                      labelId={label}
                      issue={issue}
                      projectLabels={labels}
                    />
                  ))}
                <MemoizedLabelBadge
                  key={issue.canvas.labels[0]}
                  labelId={issue.canvas.labels[0]}
                  issue={issue}
                  projectLabels={labels}
                  labelsToStack={(
                    issue.canvas.labels.map((label) =>
                      labels?.find((l) => l.id === label),
                    ) ?? []
                  ).slice(PRE_STACK_LABEL_COUNT)}
                />
              </>
            )
          ) : (
            <></>
          )}
          {/* Due Date badge - last badge */}
          {issue.featurePrd?.due_date && issue.featurePrd.due_date !== "" && (
            <MemoizedDueDateBadge
              dueDate={issue.featurePrd.due_date}
              issueId={issue.canvas.id}
            />
          )}
        </div>
      </div>
    );
  },
);

IssueGrid.displayName = "IssueGrid";
